/**
 * Gráficos del panel de administración, en SVG propio (sin librerías), según la
 * guía de visualización: un solo eje, trazos finos (línea de 2 px, barras de
 * máximo 24 px con la punta redondeada y la base recta), rejilla recesiva, tooltip
 * al pasar el cursor o con las flechas del teclado, y siempre una tabla con los
 * mismos datos. Los colores salen de los tokens --viz-* (validados en claro y
 * oscuro) y el texto nunca usa el color de la serie.
 */
import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from 'react'
import { diaCorto, pct, ticksY } from '../lib/adminFormat'

export interface Punto {
  /** Día `YYYY-MM-DD` (o una etiqueta corta). */
  etiqueta: string
  valor: number
}

/**
 * Ancho del contenedor (para dibujar en píxeles reales y que el texto no se deforme).
 * Callback ref: «Ver tabla» desmonta el gráfico y al volver se observa el div nuevo.
 */
function useAncho<T extends HTMLElement>(inicial = 640) {
  const [ancho, setAncho] = useState(inicial)
  const obs = useRef<ResizeObserver | null>(null)
  const ref = useCallback((el: T | null) => {
    obs.current?.disconnect()
    obs.current = null
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => {
      // Un elemento que se está desmontando informa 0: no es un ancho real
      const w = e ? Math.floor(e.contentRect.width) : 0
      if (w > 0) setAncho(Math.max(240, w))
    })
    ro.observe(el)
    obs.current = ro
  }, [])
  return { ref, ancho }
}

const M = { izq: 48, der: 16, arr: 14, aba: 26 }

/** Rectángulo con la punta redondeada (4 px) y la base recta, creciendo desde `base`. */
function barra(x: number, y: number, w: number, base: number, r = 4): string {
  const h = base - y
  if (h <= 0) return ''
  const rr = Math.min(r, w / 2, h)
  return `M${x},${base}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${base}Z`
}

/** Ancho mínimo entre etiquetas del eje X («26 sept» a 11 px ≈ 44 px más aire). */
const SEPARACION_X = 64

/** Etiquetas del eje X: la primera, la última y algunas entremedio, sin chocar. */
export function etiquetasX(n: number, anchoUtil: number): number[] {
  if (n <= 0) return []
  if (n === 1) return [0]
  const banda = anchoUtil / n
  const caben = Math.max(2, Math.floor(anchoUtil / SEPARACION_X))
  const paso = Math.ceil((n - 1) / (caben - 1))
  const out: number[] = []
  for (let i = 0; i < n; i += paso) out.push(i)
  if (out[out.length - 1] !== n - 1) {
    // Si la última quedaría encima de la anterior (en píxeles), la reemplaza
    if ((n - 1 - out[out.length - 1]!) * banda < SEPARACION_X && out.length > 1) out.pop()
    out.push(n - 1)
  }
  return out
}

function SinDatos() {
  return <p className="chart-vacio faint">Sin datos en el período.</p>
}

const etiquetaLegible = (e: string) => (/^\d{4}-\d{2}-\d{2}$/.test(e) ? diaCorto(e) : e)

function Tabla({
  titulo,
  columnas,
  filas
}: {
  titulo: string
  columnas: string[]
  filas: (string | number)[][]
}) {
  return (
    <div className="chart-table">
      <table className="simple">
        <caption className="sr">{titulo}</caption>
        <thead>
          <tr>
            {columnas.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>
              {f.map((v, j) => (
                <td key={j} className={j > 0 ? 'num' : undefined}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Marco({
  titulo,
  subtitulo,
  acciones,
  tabla,
  children
}: {
  titulo: string
  subtitulo?: ReactNode
  acciones?: ReactNode
  tabla: ReactNode
  children: ReactNode
}) {
  const [verTabla, setVerTabla] = useState(false)
  return (
    <figure className="chart">
      <figcaption className="chart-head">
        <div className="grow">
          <div className="chart-title">{titulo}</div>
          {subtitulo && <div className="chart-sub">{subtitulo}</div>}
        </div>
        {acciones}
        <button
          type="button"
          className="btn sm ghost"
          aria-pressed={verTabla}
          onClick={() => setVerTabla((v) => !v)}
        >
          {verTabla ? 'Ver gráfico' : 'Ver tabla'}
        </button>
      </figcaption>
      {verTabla ? tabla : children}
    </figure>
  )
}

/**
 * Hover y teclado comunes: índice activo, puntero sobre el área y flechas. El
 * objetivo es toda la franja del día (más grande que la marca).
 */
function useActivo(n: number) {
  const [activo, setActivo] = useState<number | null>(null)
  const onKeyDown = (e: KeyboardEvent) => {
    if (!n) return
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault()
      setActivo((a) => {
        const base = a ?? (e.key === 'ArrowRight' ? -1 : n)
        return Math.min(n - 1, Math.max(0, base + (e.key === 'ArrowRight' ? 1 : -1)))
      })
    } else if (e.key === 'Home') setActivo(0)
    else if (e.key === 'End') setActivo(n - 1)
    else if (e.key === 'Escape') setActivo(null)
  }
  return { activo, setActivo, onKeyDown }
}

export function GraficoSerie({
  titulo,
  subtitulo,
  puntos,
  formato,
  tipo = 'linea',
  alto = 190,
  nombreValor = 'Valor',
  acciones,
  formatoEje,
  sinTotal = false
}: {
  titulo: string
  subtitulo?: ReactNode
  puntos: Punto[]
  formato: (n: number) => string
  /** Formato de los ticks del eje Y (por defecto, el mismo de los valores). */
  formatoEje?: (n: number) => string
  tipo?: 'linea' | 'barras'
  alto?: number
  nombreValor?: string
  acciones?: ReactNode
  /** Para valores que no se suman (medianas, porcentajes): el resumen no dice «total». */
  sinTotal?: boolean
}) {
  const { ref, ancho } = useAncho<HTMLDivElement>()
  const n = puntos.length
  const { activo, setActivo, onKeyDown } = useActivo(n)
  const ticks = useMemo(() => ticksY(Math.max(0, ...puntos.map((p) => p.valor))), [puntos])
  const tope = ticks[ticks.length - 1] ?? 1
  const util = ancho - M.izq - M.der
  const base = alto - M.aba
  const banda = n ? util / n : util
  const x = (i: number) => M.izq + banda * i + banda / 2
  const y = (v: number) => M.arr + (base - M.arr) * (1 - v / tope)
  const linea = puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.valor)}`).join('')
  const area = n ? `${linea}L${x(n - 1)},${base}L${x(0)},${base}Z` : ''
  const ancho_barra = Math.min(24, Math.max(2, banda - 2))
  const sel = activo !== null ? puntos[activo] : undefined
  const total = puntos.reduce((s, p) => s + p.valor, 0)
  const id = useId()
  return (
    <Marco
      titulo={titulo}
      subtitulo={subtitulo}
      acciones={acciones}
      tabla={
        <Tabla
          titulo={titulo}
          columnas={['Día', nombreValor]}
          filas={puntos.map((p) => [etiquetaLegible(p.etiqueta), formato(p.valor)])}
        />
      }
    >
      {n === 0 ? (
        <SinDatos />
      ) : (
        <div
          ref={ref}
          className="chart-plot"
          tabIndex={0}
          role="img"
          aria-roledescription="gráfico"
          aria-label={`${titulo}: ${n} días${sinTotal ? '' : `, total ${formato(total)}`}. Usa las flechas para recorrerlo o «Ver tabla».`}
          aria-describedby={sel ? `${id}-tip` : undefined}
          onKeyDown={onKeyDown}
          onBlur={() => setActivo(null)}
        >
          <svg
            width={ancho}
            height={alto}
            onPointerMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              const i = Math.floor((e.clientX - r.left - M.izq) / banda)
              setActivo(i >= 0 && i < n ? i : null)
            }}
            onPointerLeave={() => setActivo(null)}
          >
            {ticks.map((t) => (
              <g key={t}>
                <line className="grid" x1={M.izq} x2={ancho - M.der} y1={y(t)} y2={y(t)} />
                <text className="axis" x={M.izq - 8} y={y(t)} dy="0.32em" textAnchor="end">
                  {(formatoEje ?? formato)(t)}
                </text>
              </g>
            ))}
            {etiquetasX(n, util).map((i) => (
              <text key={i} className="axis" x={x(i)} y={alto - 6} textAnchor="middle">
                {etiquetaLegible(puntos[i]!.etiqueta)}
              </text>
            ))}
            {tipo === 'barras'
              ? puntos.map((p, i) => (
                  <path
                    key={p.etiqueta}
                    className={`mark s1${activo !== null && activo !== i ? ' dim' : ''}`}
                    d={barra(x(i) - ancho_barra / 2, y(p.valor), ancho_barra, base)}
                  />
                ))
              : n > 0 && (
                  <>
                    <path className="wash s1" d={area} />
                    <path className="line s1" d={linea} />
                    <circle className="dot s1" cx={x(n - 1)} cy={y(puntos[n - 1]!.valor)} r={4} />
                  </>
                )}
            {sel && activo !== null && (
              <g>
                <line className="cross" x1={x(activo)} x2={x(activo)} y1={M.arr} y2={base} />
                {tipo === 'linea' && (
                  <circle className="dot s1" cx={x(activo)} cy={y(sel.valor)} r={5} />
                )}
              </g>
            )}
          </svg>
          {sel && activo !== null && (
            <div
              id={`${id}-tip`}
              className="chart-tip"
              role="status"
              style={{
                left: Math.min(Math.max(x(activo), 70), ancho - 70),
                top: Math.max(0, y(sel.valor) - 58)
              }}
            >
              <div className="faint">{etiquetaLegible(sel.etiqueta)}</div>
              <strong>{formato(sel.valor)}</strong>
            </div>
          )}
        </div>
      )}
    </Marco>
  )
}

export interface SerieApilada {
  clave: string
  nombre: string
  /** Índice fijo de color (1–4): el color sigue a la entidad, no a su orden. */
  color: 1 | 2 | 3 | 4
}

export function BarrasApiladas({
  titulo,
  subtitulo,
  dias,
  series,
  valores,
  formato,
  formatoEje,
  alto = 200
}: {
  titulo: string
  subtitulo?: ReactNode
  dias: string[]
  series: SerieApilada[]
  /** valores[dia][clave] */
  valores: Record<string, Record<string, number>>
  formato: (n: number) => string
  formatoEje?: (n: number) => string
  alto?: number
}) {
  const { ref, ancho } = useAncho<HTMLDivElement>()
  const n = dias.length
  const { activo, setActivo, onKeyDown } = useActivo(n)
  const totales = dias.map((d) => series.reduce((s, x) => s + (valores[d]?.[x.clave] ?? 0), 0))
  const ticks = ticksY(Math.max(0, ...totales))
  const tope = ticks[ticks.length - 1] ?? 1
  const util = ancho - M.izq - M.der
  const base = alto - M.aba
  const banda = n ? util / n : util
  const w = Math.min(24, Math.max(2, banda - 2))
  const alturaDe = (v: number) => (base - M.arr) * (v / tope)
  const id = useId()
  const sel = activo !== null ? dias[activo] : undefined
  return (
    <Marco
      titulo={titulo}
      subtitulo={subtitulo}
      acciones={
        <ul className="legend" aria-label="Leyenda">
          {series.map((s) => (
            <li key={s.clave}>
              <span className={`swatch s${s.color}`} aria-hidden="true" />
              {s.nombre}
            </li>
          ))}
        </ul>
      }
      tabla={
        <Tabla
          titulo={titulo}
          columnas={['Día', ...series.map((s) => s.nombre), 'Total']}
          filas={dias.map((d, i) => [
            etiquetaLegible(d),
            ...series.map((s) => formato(valores[d]?.[s.clave] ?? 0)),
            formato(totales[i]!)
          ])}
        />
      }
    >
      {n === 0 ? (
        <SinDatos />
      ) : (
        <div
          ref={ref}
          className="chart-plot"
          tabIndex={0}
          role="img"
          aria-roledescription="gráfico"
          aria-label={`${titulo}: ${n} días. Usa las flechas para recorrerlo o «Ver tabla».`}
          aria-describedby={sel ? `${id}-tip` : undefined}
          onKeyDown={onKeyDown}
          onBlur={() => setActivo(null)}
        >
          <svg
            width={ancho}
            height={alto}
            onPointerMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              const i = Math.floor((e.clientX - r.left - M.izq) / banda)
              setActivo(i >= 0 && i < n ? i : null)
            }}
            onPointerLeave={() => setActivo(null)}
          >
            {ticks.map((t) => {
              const yy = base - alturaDe(t)
              return (
                <g key={t}>
                  <line className="grid" x1={M.izq} x2={ancho - M.der} y1={yy} y2={yy} />
                  <text className="axis" x={M.izq - 8} y={yy} dy="0.32em" textAnchor="end">
                    {(formatoEje ?? formato)(t)}
                  </text>
                </g>
              )
            })}
            {etiquetasX(n, util).map((i) => (
              <text
                key={i}
                className="axis"
                x={M.izq + banda * i + banda / 2}
                y={alto - 6}
                textAnchor="middle"
              >
                {etiquetaLegible(dias[i]!)}
              </text>
            ))}
            {dias.map((d, i) => {
              const cx = M.izq + banda * i + banda / 2 - w / 2
              let techo = base
              const presentes = series.filter((s) => (valores[d]?.[s.clave] ?? 0) > 0)
              return (
                <g key={d} className={activo !== null && activo !== i ? 'dim' : undefined}>
                  {presentes.map((s, k) => {
                    const h = alturaDe(valores[d]![s.clave]!)
                    // 2 px de superficie entre segmentos; solo el de arriba lleva punta redondeada
                    const gap = k > 0 ? 2 : 0
                    const y0 = techo - gap
                    techo = y0 - h
                    const d0 =
                      k === presentes.length - 1
                        ? barra(cx, techo, w, y0)
                        : `M${cx},${y0}V${techo}H${cx + w}V${y0}Z`
                    return <path key={s.clave} className={`mark s${s.color}`} d={d0} />
                  })}
                </g>
              )
            })}
          </svg>
          {sel && activo !== null && (
            <div
              id={`${id}-tip`}
              className="chart-tip"
              role="status"
              style={{
                left: Math.min(Math.max(M.izq + banda * activo + banda / 2, 90), ancho - 90),
                top: 0
              }}
            >
              <div className="faint">{etiquetaLegible(sel)}</div>
              {series.map((s) => (
                <div key={s.clave} className="row" style={{ gap: 6 }}>
                  <span className={`swatch s${s.color}`} aria-hidden="true" />
                  <span className="grow">{s.nombre}</span>
                  <strong>{formato(valores[sel]?.[s.clave] ?? 0)}</strong>
                </div>
              ))}
              <div
                className="row"
                style={{ gap: 6, borderTop: '1px solid var(--line)', paddingTop: 4 }}
              >
                <span className="grow">Total</span>
                <strong>{formato(totales[activo]!)}</strong>
              </div>
            </div>
          )}
        </div>
      )}
    </Marco>
  )
}

/** Minigráfico de tendencia para una tarjeta: tono apagado y el último punto en acento. */
export function Minigrafico({ valores, etiqueta }: { valores: number[]; etiqueta: string }) {
  const w = 96
  const h = 28
  if (valores.length < 2) return null
  // Con valores chicos (US$ 0,40 al día) la línea igual usa todo el alto
  const max = Math.max(...valores) || 1
  const x = (i: number) => (i / (valores.length - 1)) * (w - 4) + 2
  const y = (v: number) => h - 3 - (v / max) * (h - 6)
  const d = valores.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join('')
  return (
    <svg className="spark" width={w} height={h} role="img" aria-label={etiqueta}>
      <path d={d} />
      <circle cx={x(valores.length - 1)} cy={y(valores[valores.length - 1]!)} r={3} />
    </svg>
  )
}

/**
 * Cohortes semanales: una fila por semana de registro; cada celda, el % de esa
 * cohorte con reuniones en la semana k. Rampa secuencial de un solo tono
 * (terracota, de claro a oscuro); el texto elige tinta o blanco según el fondo.
 */
export function MapaCohortes({
  cohortes
}: {
  cohortes: { semana: string; usuarios: number; activos: (number | null)[] }[]
}) {
  const columnas = cohortes[0]?.activos.length ?? 0
  return (
    <figure className="chart">
      <figcaption className="chart-head">
        <div className="grow">
          <div className="chart-title">Retención por semana de registro</div>
          <div className="chart-sub">
            Qué parte de cada grupo tuvo alguna reunión en su semana 0, 1, 2… desde que se registró.
          </div>
        </div>
      </figcaption>
      <div className="cohort-wrap">
        <table className="cohort">
          <caption className="sr">Retención semanal por cohorte de registro</caption>
          <thead>
            <tr>
              <th scope="col">Semana</th>
              <th scope="col" className="num">
                Personas
              </th>
              {Array.from({ length: columnas }, (_, k) => (
                <th key={k} scope="col" className="num">
                  S{k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohortes.map((c) => (
              <tr key={c.semana}>
                <th scope="row">{diaCorto(c.semana)}</th>
                <td className="num">{c.usuarios}</td>
                {c.activos.map((a, k) => {
                  if (a === null) return <td key={k} className="cell futura" aria-label="aún no" />
                  const p = c.usuarios ? a / c.usuarios : 0
                  const nivel = c.usuarios === 0 ? 0 : Math.min(5, Math.ceil(p * 5))
                  return (
                    <td key={k} className={`cell n${nivel}`} title={`${a} de ${c.usuarios}`}>
                      {c.usuarios ? pct(a, c.usuarios) : '—'}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  )
}

/** Embudo: barras horizontales proporcionales al primer paso, con % sobre él. */
export function Embudo({ pasos }: { pasos: { nombre: string; valor: number; ayuda?: string }[] }) {
  const tope = Math.max(1, pasos[0]?.valor ?? 1)
  return (
    <ol className="funnel">
      {pasos.map((p, i) => (
        <li key={p.nombre}>
          <div className="row" style={{ gap: 8 }}>
            <span className="grow">
              {p.nombre}
              {p.ayuda && <span className="faint"> · {p.ayuda}</span>}
            </span>
            <strong className="num">{p.valor.toLocaleString('es-CL')}</strong>
            <span className="faint num" style={{ width: 48, textAlign: 'right' }}>
              {i === 0 ? '' : pct(p.valor, pasos[0]!.valor)}
            </span>
          </div>
          <div className="funnel-bar" aria-hidden="true">
            <span style={{ width: `${(p.valor / tope) * 100}%` }} />
          </div>
        </li>
      ))}
    </ol>
  )
}
